import React from 'react';
import { EducationFormatter } from '@/utils/educationFormatter';

interface ResumeData {
  personalInfo: {
    name: string;
    title: string;
    email: string;
    phone: string;
    location: string;
    linkedin?: string;
    website?: string;
  };
  summary: string;
  experience: Array<{
    title: string;
    company: string;
    location: string;
    dates: string;
    bullets: string[];
  }>;
  education: Array<{
    degree: string;
    school: string;
    location: string;
    dates: string;
    gpa?: string;
  }>;
  skills: string[];
}

interface Template8Props {
  data: ResumeData;
}

export const Template8: React.FC<Template8Props> = ({ data }) => {
  return (
    <div className="max-w-4xl mx-auto bg-white text-gray-900 font-sans">
      {/* Executive Header */}
      <header className="border-b border-gray-200 pb-8 mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-5xl font-bold text-gray-900 mb-2">{data.personalInfo.name}</h1>
            <h2 className="text-2xl text-amber-600 font-medium mb-4">{data.personalInfo.title}</h2>
          </div>
          <div className="text-right space-y-1 text-sm text-gray-600">
            <p>{data.personalInfo.email}</p>
            <p>{data.personalInfo.phone}</p>
            <p>{data.personalInfo.location}</p>
            {data.personalInfo.linkedin && <p>{data.personalInfo.linkedin}</p>}
            {data.personalInfo.website && <p>{data.personalInfo.website}</p>}
          </div>
        </div>
      </header>

      {/* Executive Summary */}
      <section className="mb-10">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">EXECUTIVE SUMMARY</h3>
        <div className="bg-amber-50 border-l-8 border-amber-500 p-6">
          <p className="text-gray-700 leading-relaxed text-lg">{data.summary}</p>
        </div>
      </section>

      {/* Professional Experience */}
      <section className="mb-10">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">PROFESSIONAL EXPERIENCE</h3>
        
        {data.experience.map((job, index) => (
          <div key={index} className="mb-10 pb-8 border-b border-gray-100 last:border-b-0">
            <div className="mb-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-xl font-bold text-gray-900">{job.title}</h4>
                <div className="bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-bold">
                  {job.dates}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-xl text-amber-600 font-semibold">{job.company}</p>
                <p className="text-gray-600">{job.location}</p>
              </div>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="font-semibold text-gray-900 mb-3">Key Achievements:</h5>
              <ul className="space-y-3">
                {job.bullets.map((bullet, bulletIndex) => (
                  <li key={bulletIndex} className="flex items-start">
                    <div className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold mr-4 mt-0.5 flex-shrink-0">
                      {bulletIndex + 1}
                    </div>
                    <span className="text-gray-700 font-medium">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </section>

      {/* Education & Core Competencies */}
      <div className="grid grid-cols-2 gap-12">
        {/* Education */}
        <section>
          <h3 className="text-2xl font-bold text-gray-900 mb-6">EDUCATION</h3>
          
          <EducationFormatter 
            education={data.education} 
            className="text-gray-900"
          />
        </section>

        {/* Skills */}
        <section>
          <h3 className="text-2xl font-bold text-gray-900 mb-6">CORE COMPETENCIES</h3>
          
          <div className="space-y-4">
            {data.skills.map((skill, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                <span className="text-gray-900 font-medium">{skill}</span>
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <div key={star} className="w-3 h-3 bg-amber-500 rounded-full"></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};